import React from 'react';
import { Button } from '@bedrock/components';
import CopyIcon from '@mpc/common/src/components/CopyIcon';
import createFormItemRow, {
  type FormItemProp,
} from '@mpc/common/src/components/SForm/createFormItemRow';
import { $sType } from '@mpc/common/src/utils/sType';
import { openDetailPath } from '@mpc/common/src/utils/openDetail';
import { userSelectFormat, CHANNEL_L1_CATEGORIES, PLATFORM_L1_CATEGORIES } from '../constants';
import RechargeDemandStateTag from '../../RechargeRefund/Drawer/RechargeDemandStateTag';
import { Z_INDEX } from '../../../utils/zIndex';

/**
 * 退款 FormModel — 用 SForm 的 createFormItemRow 搭建
 *
 * 范式锚点：PrePayment/FormModel/index.tsx:46（createFormItemRow 产物）
 * 设计依据：mixspec/changes/16366-02-退款流程/frontend-design.md §3.2（基于设计稿实测）
 * 字段选型经用户确认：currencyType/textarea/select/input(view+showComponent)/upload
 *
 * 只读字段（二级单号/三级单号/退款单号/投放渠道/预算金额/执行日期）用 view:true + showComponent
 * 自定义展示，可编辑字段（退款金额/充值账号ID/优化师/附件）正常渲染。
 *
 * 单一字段源：refundFormModelFields 为纯数据配置，同时被
 * - 单条表单 createFormItemRow(refundFormModelFields)（SForm 消费）
 * - 批量表格 formModelToColumns(refundFormModelFields)（ProTableForm formColumns 消费）
 * 复用，达到模型和交互逻辑对齐（见 ./toColumns.tsx）。
 */
/** 需求单详情跳转（镜像 PrePayment）：内包渠道单详情 / 外包常规采购发起入口 */
export const openDemandDetail = (demandId: number | string) => {
  if ($sType === 'inner') {
    // 统一跳转工具（对齐 MyWorkBench 聚合列表二级单/三级单跳转，openDetail.ts openDetailPath）
    openDetailPath('CHANNEL_GROUP', String(demandId));
  }
  // 外包供应商端：无需求详情跳转路由（原 window.open 外包地址不存在/不对），保持展示不跳转
};

export const refundFormModelFields: Record<string, FormItemProp> = {
  l2_demand_id: {
    label: '二级单号',
    col: 12,
    view: true,
    componentClass: 'input',
    show: true,
    elementProps: ({ detailData }: { detailData: any }) => ({
      showComponent: () => {
        const text = detailData?.l2_demand_id;
        const stateName = detailData?.l2_demand_state_name;
        const idRow = text ? (
          // 外包供应商端：单号仅展示可复制，不跳转需求详情（外包无对应需求详情路由，openDemandDetail 外包不跳转）
          $sType === 'outer' ? (
            <div className="flex items-center">
              <span>{text}</span>
              <CopyIcon text={String(text)} tipToolTitle="复制" />
            </div>
          ) : (
            <div className="flex items-center">
              <Button type="link" onClick={() => openDemandDetail(text)}>
                {text}
              </Button>
              <CopyIcon text={String(text)} tipToolTitle="复制" />
            </div>
          )
        ) : (
          <span className="text-color-3">--</span>
        );
        return (
          <div className="flex flex-col gap-1">
            {idRow}
            {stateName ? (
              <RechargeDemandStateTag
                state={detailData?.l2_demand_state}
                stateName={stateName}
              />
            ) : null}
          </div>
        );
      },
    }),
  },
  l3_demand_id: {
    label: '三级单号',
    col: 12,
    view: true,
    componentClass: 'input',
    show: true,
    elementProps: ({ detailData }: { detailData: any }) => ({
      showComponent: () => {
        const text = detailData?.l3_demand_id;
        const stateName = detailData?.l3_demand_state_name;
        const idRow = text ? (
          // 外包供应商端：单号仅展示可复制，不跳转需求详情（外包无对应需求详情路由，openDemandDetail 外包不跳转）
          $sType === 'outer' ? (
            <div className="flex items-center">
              <span>{text}</span>
              <CopyIcon text={String(text)} tipToolTitle="复制" />
            </div>
          ) : (
            <div className="flex items-center">
              <Button type="link" onClick={() => openDemandDetail(text)}>
                {text}
              </Button>
              <CopyIcon text={String(text)} tipToolTitle="复制" />
            </div>
          )
        ) : (
          <span className="text-color-3">--</span>
        );
        return (
          <div className="flex flex-col gap-1">
            {idRow}
            {stateName ? (
              <RechargeDemandStateTag
                state={detailData?.l3_demand_state}
                stateName={stateName}
              />
            ) : null}
          </div>
        );
      },
    }),
  },
  refund_no: {
    label: '退款单号',
    col: 12,
    view: true,
    componentClass: 'input',
    show: true,
    elementProps: ({ detailData }: { detailData: any }) => ({
      showComponent: () => {
        const text = detailData?.refund_no;
        return text ? (
          <div className="flex items-center">
            <span>{text}</span>
            <CopyIcon text={text} tipToolTitle="复制" />
          </div>
        ) : (
          <span className="text-color-3">自动生成</span>
        );
      },
    }),
  },
  $refund_amount: {
    label: '退款金额',
    col: 12,
    required: true,
    componentClass: 'currencyType',
    // 可编辑字段：view 由调用方（CreateRefundModal itemCols）控制，此处不设 view:true
    // 货币类型固定：与预算金额同源（formItemKeyMap currency→'currency'，单的结算币种），编辑退款金额时不可改币种
    // 币种下拉用组件默认 currencyEnum（CurrencyType.tsx:67 objectTransenum(currencyEnum)），不传 currencyOptions（无效 prop）
    elementProps: {
      selectDisabled: true,
    },
    formItemKeyMap: {
      value: 'amount',
      currency: 'currency',
    },
  },
  account_id_name: {
    label: '退款账号ID/账号名',
    col: 24,
    // POPO：国内非必填 / 海外必填（region 联动，镜像充值 formConfig isFieldRequired ACCOUNT_ID_NAME）
    // required 读 region：优先 formData（用户改动），兜底 detailData（草稿返回的 region 一定准确；
    // create 时 region 为只读字段经 setFieldsValue 注入，不触发 onValuesChange，formData 可能未同步，故需 detailData 兜底）
    required: ({ formData, detailData }: { formData: any; detailData?: any }) =>
      (formData?.region ?? detailData?.region ?? 'DOMESTIC') === 'OVERSEA',
    // 单个详情编辑态用 input（单行，Design Feedback 2026-08-24）；批量表格共享 FormModel
    componentClass: 'input',
    // 可编辑字段：view 由调用方（CreateRefundModal itemCols）控制，此处不设 view:true
    elementProps: {
      maxLength: 200,
      showCount: true,
      placeholder: '请输入',
    },
  },
  channel: {
    label: '投放渠道',
    col: 12,
    componentClass: 'select',
    // POPO：仅效果买量/收口投放/品效推广显示（l1_category_id 判断）；可编辑下拉（可选范围=草稿 channel_options）
    show: ({ detailData }: { detailData: any }) =>
      CHANNEL_L1_CATEGORIES.has(Number(detailData?.l1_category_id)),
    // POPO：显示时策划/优化师必填、供应商非必填（镜像充值 formConfig isFieldRequired CHANNEL）
    required: ({ formData }: { formData: any }) => {
      const visible = CHANNEL_L1_CATEGORIES.has(Number(formData?.l1_category_id));
      return visible && formData?.initiator_role !== 'SUPPLIER';
    },
    elementProps: ({ detailData }: { detailData: any }) => ({
      options: (detailData?.channel_options ?? []).map((v: string) => ({ value: v, name: v })),
      placeholder: '请选择投放渠道',
      allowClear: true,
      showSearch: true,
      // 下拉弹层 zIndex 高于抽屉（Z_INDEX.drawerPopup），避免被抽屉自身盖住看不到选项（对齐优化师）
      dropdownStyle: { zIndex: Z_INDEX.drawerPopup },
    }),
  },
  platform: {
    label: '合作平台',
    col: 12,
    componentClass: 'select',
    // POPO：仅喇叭体系显示；可编辑下拉（可选范围=草稿 platform_options）
    show: ({ detailData }: { detailData: any }) =>
      PLATFORM_L1_CATEGORIES.has(Number(detailData?.l1_category_id)),
    required: ({ formData }: { formData: any }) => {
      const visible = PLATFORM_L1_CATEGORIES.has(Number(formData?.l1_category_id));
      return visible && formData?.initiator_role !== 'SUPPLIER';
    },
    elementProps: ({ detailData }: { detailData: any }) => ({
      options: (detailData?.platform_options ?? []).map((v: string) => ({ value: v, name: v })),
      placeholder: '请选择合作平台',
      allowClear: true,
      showSearch: true,
      // 下拉弹层 zIndex 高于抽屉（对齐优化师，Z_INDEX.drawerPopup），避免被盖住看不到选项
      dropdownStyle: { zIndex: Z_INDEX.drawerPopup },
    }),
  },
  $budget_amount: {
    label: '预算金额',
    col: 12,
    view: true,
    componentClass: 'currencyType',
    // 只读展示：复用 CurrencyType view 模式（金额千分位 + 币种 label），不手动拼接（对齐 PrePayment $prepayment_amount）
    elementProps: {
      selectDisabled: true,
    },
    formItemKeyMap: {
      value: 'budget',
      currency: 'currency',
    },
  },
  execute_date_start: {
    label: '执行日期',
    col: 12,
    view: true,
    componentClass: 'input',
    show: true,
    elementProps: ({ detailData }: { detailData: any }) => ({
      showComponent: () => {
        // 执行日期为区间（二级单 expect_begin_at ~ expect_end_at）
        const start = detailData?.execute_date_start;
        const end = detailData?.execute_date_end;
        return start ? (
          <span>{end ? `${start} ~ ${end}` : start}</span>
        ) : (
          <span className="text-color-3">--</span>
        );
      },
    }),
  },
  optimizers: {
    label: '优化师',
    col: 12,
    required: true,
    componentClass: 'select',
    // 详情模型用 optimizers（人员对象数组），提交时由调用方转 optimizer_ids（id 数组）——对齐后端契约
    // options 只取 FormDrawer 注入的真实优化师（useRefundOptimizerOptions，FR-14，走 renderData.optionMap.optimizer）；
    // 未注入/查询为空 → 空数组（不回落 mock，避免未填写时展示假优化师）
    elementProps: ({
      formData,
      optionMap,
    }: {
      formData: any;
      optionMap?: Record<string, any[]>;
    }) => {
      const region = formData?.region;
      const isDomestic = region === 'DOMESTIC' || !region;
      return {
        options: optionMap?.optimizer ?? [],
        // 下拉弹层 zIndex 高于抽屉（Z_INDEX.drawerPopup），避免被抽屉自身盖住看不到选项
        dropdownStyle: { zIndex: Z_INDEX.drawerPopup },
        showSearch: true,
        allowClear: true,
        multiple: true,
        // 人员类型下拉：selectFormat 把选中 user_id 映射为对象（对齐 TrumpetDetailFormModel userSelectFormat）
        selectFormat: userSelectFormat,
        required: isDomestic,
        placeholder: isDomestic ? '请选择优化师（国内必填）' : '海外可不填',
        // view 回显：直接用已选优化师 name join（不依赖 options 反查 findOption——外包 options
        // 查询为空或 value 含空对象时，通用 select view 会 join 出孤立顿号/逗号，如「，」）
        showComponent: (value: any) => {
          const list = (value ?? []).filter((o: any) => o?.name);
          return list.length ? list.map((o: any) => o.name).join('、') : '--';
        },
      };
    },
  },
  attachments: {
    label: '退款说明/退款附件',
    col: 24,
    required: false,
    // 复合组件 value={text, files}：TextAreaWithUpload（文本+附件，PRD 长文本+附件）
    // formItemKeyMap 映射到后端两字段（分开存储）：text→refund_remark（退款说明）、files→refund_attachments（退款附件）
    componentClass: 'textWithUpload',
    formItemKeyMap: {
      text: 'refund_remark',
      files: 'refund_attachments',
    },
    // 至少一个不为空：退款说明与退款附件至少填一个（validator 校验 {text, files}，不依赖 watchChildFormValidate ——
    // SFormElement 非 forwardRef 不透传 ref 到 TextAreaWithUpload）
    rules: [
      {
        validator: (
          _rule: unknown,
          value: { text?: string; files?: unknown[] } | undefined
        ) => {
          const v = value || {};
          if (v.text || v.files?.length) return Promise.resolve();
          return Promise.reject(new Error('退款说明与退款附件至少一个不为空'));
        },
      },
    ],
    elementProps: {
      // 单个详情/发起用 block 布局（正常 textarea + SUpload 上下直列）；批量表格 override 回 inline
      displayType: 'block',
      textAreaProps: {
        maxLength: 500,
        placeholder: '请输入退款说明',
        showCount: true,
      },
      maxFileSize: 200 * 1024 * 1024,
    },
  },
};

const RefundFormModel = createFormItemRow(refundFormModelFields);

export default RefundFormModel;
